import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Center,
  Circle,
  Float,
  HStack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import UserIcon from '@/_assets/icon-components/User';
// import HomeIcon from '@/_assets/icon-components/Home';
import CartIcon from '@/_assets/icon-components/Cart';
import { Link, usePathname } from '@/i18n/navigation';
import orderService from '@/_service/orders.service';
import { IRedux } from '@/_store/types';
import Auth from '@/_components/Auth';
import { useQuery } from '@tanstack/react-query';
import { SearchFilledIcon, SearchOutlineIcon } from '@/_assets/icon-components/HomeSearch';
import { HomeIcon, HomeFilledIcon } from '@/_assets/icon-components/Home';

type NavItem = {
  href: string;
  title: string;
  icon: (color: string) => React.ReactNode;
  iconActive?: (color: string) => React.ReactNode;
};

const NavigationMobile = ({ isTgBot = false }: { isTgBot?: boolean }) => {
  const [activeOrderCount, setActiveOrderCount] = useState(0);
  const [optimisticIndex, setOptimisticIndex] = useState<number | null>(null);

  const auth = useSelector((state: IRedux) => state.auth);
  const cartState = useSelector((state: IRedux) => state.cart);

  // Flatten all products from all brands into a single array
  const cart = useMemo(() => {
    if (
      !cartState?.cart ||
      typeof cartState.cart !== 'object' ||
      Array.isArray(cartState.cart)
    ) {
      return [];
    }
    return Object.values(cartState.cart).flatMap(
      (brandCart: any) => brandCart?.products || []
    );
  }, [cartState?.cart]);

  const { open, onClose, onOpen } = useDisclosure();
  const pathname = usePathname();
  const t = useTranslations();

  const { data, refetch } = useQuery({
    queryKey: ['get-active-order-count'],
    queryFn: () => orderService.getActiveCount().then((res) => res.data),
    enabled: Boolean(auth?.user),
  });

  useEffect(() => {
    if (data?.count) {
      setActiveOrderCount(Number(data?.count));
    }
  }, [data]);

  const items: NavItem[] = [
    {
      href: '/',
      title: 'home',
      icon: (color: string) => <HomeIcon color={color} />,
      iconActive: (color: string) => <HomeFilledIcon color={color} />, // optional: use when you have a different active icon
    },
    {
      href: `/where-to-go`,
      title: 'where_to_go',
      icon: (color: string) => <SearchOutlineIcon color={color} />,
      iconActive: (color: string) => <SearchFilledIcon color={color} />,
    },
    {
      href: `/cart`,
      title: 'cart',
      icon: (color: string) => <CartIcon color={color} w='24' h='24' />,
    },
    {
      href: '/profile',
      title: 'profile',
      icon: (color: string) => <UserIcon color={color} w='24' h='24' />,
    },
  ];

  // Derive active index from current pathname
  const pathnameBasedIndex = items.findIndex((item) => {
    if (item.href === '/') return pathname === '/';
    return pathname.startsWith(item.href);
  });

  // Use optimistic index for instant feedback, fall back to pathname-based
  const activeIndex =
    optimisticIndex !== null ? optimisticIndex : pathnameBasedIndex;

  // Sync optimistic state with actual pathname changes
  useEffect(() => {
    setOptimisticIndex(null);
  }, [pathname]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (auth?.user) {
      refetch();
      timeoutId = setTimeout(() => {
        refetch();
      }, 10000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [pathname, auth?.user]);

  return (
    <>
      <Box
        display={{ base: 'block', lg: 'none' }}
        position='fixed'
        bottom={0}
        left={0}
        right={0}
        width='100vw'
        zIndex={30}
        bg='white'
        borderTop='1px solid'
        borderColor='rgba(0,0,0,0.06)'
      >
        <HStack height='70px' gap={0} justifyContent='space-around' px={2}>
          {items.map((item, index) => {
            const isActive = index === activeIndex;
            const requiresAuth = 'requiresAuth' in item && item.requiresAuth;
            const shouldPreventDefault = requiresAuth && !auth?.user;

            return (
              <Center
                key={item.title + index}
                flex={1}
                flexDir='column'
                gap={4}
              >
                <Link
                  href={item.href}
                  onClick={(e) => {
                    if (shouldPreventDefault) {
                      e.preventDefault();
                      onOpen();
                    } else {
                      setOptimisticIndex(index);
                    }
                  }}
                  prefetch={true}
                >
                  <Box
                    position='relative'
                    display='flex'
                    flexDir='column'
                    alignItems='center'
                    gap={'2px'}
                  >
                    {item.title === 'cart' && cart?.length > 0 && (
                      <Float top={1} right={1}>
                        <Circle
                          size='4'
                          bg='primary'
                          fontSize='9px'
                          color='white'
                        >
                          {cart.length}
                        </Circle>
                      </Float>
                    )}
                    {item.title === 'orders' && activeOrderCount > 0 && (
                      <Float top={-1} right={-1}>
                        <Circle
                          size='4'
                          bg='primary'
                          fontSize='9px'
                          color='white'
                        >
                          {activeOrderCount}
                        </Circle>
                      </Float>
                    )}
                    {isActive && item.iconActive
                      ? item.iconActive('var(--primary-default)')
                      : item.icon(
                        isActive ? 'var(--primary-default)' : '#2D264B'
                      )}
                    <Text
                      textAlign='center'
                      fontSize='12px'
                      fontWeight={isActive ? 500 : 400}
                      color={isActive ? 'var(--primary-default)' : '#2D264B'}
                    >
                      {t(item.title)}
                    </Text>
                  </Box>
                </Link>
              </Center>
            );
          })}
        </HStack>
      </Box>
      <Auth open={open} onClose={onClose} />
    </>
  );
};

export default NavigationMobile;
